import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import UserCard from 'flarum/forum/components/UserCard';
import CommentPost from 'flarum/forum/components/CommentPost';
import SettingsPage from 'flarum/forum/components/SettingsPage';
import FieldSet from 'flarum/common/components/FieldSet';
import Switch from 'flarum/common/components/Switch';
import ItemList from 'flarum/common/utils/ItemList';

import extendUser from '../common/models/extendUser';
import formatPregnancyStatus from '../common/utils/formatPregnancyStatus';

app.initializers.add('happydigital-pregnancy', () => {
  extendUser();

  // Profile hero. Added as its own info item, because core lays these out as
  // `.UserCard-info > li { display: inline-block }` alongside "online"/"joined".
  extend(UserCard.prototype, 'infoItems', function (items) {
    const user = this.attrs.user;

    if (!user || !user.pregnancyShowOnProfile || !user.pregnancyShowOnProfile()) return;

    const label = formatPregnancyStatus(user);

    if (!label) return;

    items.add('pregnancy-status', <span className="PregnancyStatus">{label}</span>, 5);
  });

  // A subtitle under the author's name, so replies can account for the week.
  //
  // It is a sibling of the header rather than a child of it. Everything inside
  // `.Post-header > ul` is `display: inline` — the username and the timestamp
  // share one line — so any block placed in there breaks that line and pushes
  // the timestamp down. Sitting between the header (priority 100) and the body
  // (90) gives the subtitle its own line while leaving the header untouched.
  extend(CommentPost.prototype, 'contentItems', function (items) {
    const label = pregnancyPostLabel(this.attrs.post);

    if (!label) return;

    items.add('pregnancy-status', <div className="PregnancySubtitle">{label}</div>, 95);
  });

  // Self-service preferences live with the rest of the user's settings.
  //
  // FieldSet wraps each child in its own <li>, and .SettingsPage fieldset > ul >
  // li is what supplies the vertical rhythm, so the controls are handed over as
  // separate items exactly like core's own privacyItems().
  extend(SettingsPage.prototype, 'settingsItems', function (items) {
    if (!app.session.user) return;

    items.add(
      'pregnancy',
      FieldSet.component(
        { className: 'Settings-pregnancy', label: app.translator.trans('happydigital-pregnancy.forum.settings.heading') },
        pregnancySettingsItems(this).toArray()
      )
    );
  });
});

/**
 * The subtitle text for a post, or an empty string when none should be shown.
 *
 * @param {Post} post
 * @returns {string|Array}
 */
function pregnancyPostLabel(post) {
  const user = post && post.user();

  if (!user || !user.pregnancyShowOnPosts || !user.pregnancyShowOnPosts()) return '';

  return formatPregnancyStatus(user);
}

/**
 * The individual controls of the "Pregnancy Status" fieldset.
 *
 * @param {SettingsPage} page
 * @returns {ItemList}
 */
function pregnancySettingsItems(page) {
  const items = new ItemList();
  const user = app.session.user;

  // Loading flags live on the page so a redraw does not reset them.
  page.pregnancyLoading = page.pregnancyLoading || {};

  const save = (key, attributes) => {
    page.pregnancyLoading[key] = true;

    user
      .save(attributes)
      .catch(() => {})
      .then(() => {
        page.pregnancyLoading[key] = false;
        m.redraw();
      });
  };

  items.add(
    'showOnProfile',
    Switch.component(
      {
        state: !!user.pregnancyShowOnProfilePref(),
        loading: !!page.pregnancyLoading.showOnProfile,
        onchange: (value) => save('showOnProfile', { pregnancyShowOnProfile: value }),
      },
      app.translator.trans('happydigital-pregnancy.forum.settings.show_on_profile_label')
    ),
    100
  );

  items.add(
    'showOnPosts',
    Switch.component(
      {
        state: !!user.pregnancyShowOnPostsPref(),
        loading: !!page.pregnancyLoading.showOnPosts,
        onchange: (value) => save('showOnPosts', { pregnancyShowOnPosts: value }),
      },
      app.translator.trans('happydigital-pregnancy.forum.settings.show_on_posts_label')
    ),
    90
  );

  return items;
}
